import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getUserByEmail, getUserByUsername, createUser, updateUser } from '../services/api';
import { setAuthToken } from '../services/mongoApi';
import { clearAuthCache as clearAuthCacheUtil, CACHE_KEYS } from '../utils/cacheUtils';
import { useApp } from './AppContext';

// Define missing cache key constants
const USER_CACHE_KEY = CACHE_KEYS.USER;
const AUTH_STATE_KEY = CACHE_KEYS.AUTH_STATE;
const CACHE_TIMESTAMP_KEY = CACHE_KEYS.CACHE_TIMESTAMP;
const LAST_ACTIVITY_KEY = CACHE_KEYS.LAST_ACTIVITY;

// Define clearAuthCache function
const clearAuthCache = () => {
  clearAuthCacheUtil();
};

export const USER_ROLES = {
  ADMIN: 'ADMIN',
  MODERATOR: 'MODERATOR',
  USER: 'USER',
};

const AuthContext = React.createContext();

// Cache and session configuration
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const IDLE_CHECK_INTERVAL = 60 * 1000; // 1 minute

export const AuthProvider = ({ children }) => {
  const { addNotification } = useApp();
  const [user, setUser] = useState(() => {
    // Try to restore user from localStorage with comprehensive validation
    try {
      const cached = localStorage.getItem(CACHE_KEYS.USER);
      const authState = localStorage.getItem(CACHE_KEYS.AUTH_STATE);
      const cacheTimestamp = localStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
      const lastActivity = localStorage.getItem(CACHE_KEYS.LAST_ACTIVITY);
      
      // Check cache expiry
      if (cacheTimestamp && Date.now() - parseInt(cacheTimestamp) > CACHE_EXPIRY_TIME) {
        clearAuthCache();
        return null;
      }
      
      // Check session timeout
      if (lastActivity && Date.now() - parseInt(lastActivity) > SESSION_TIMEOUT) {
        clearAuthCache();
        return null;
      }
      
      if (cached && authState === 'authenticated') {
        const userData = JSON.parse(cached);
        // Validate cached data has required fields and structure
        if (userData?.id && userData?.email && typeof userData === 'object') {
          // Update last activity
          localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
          return userData;
        }
      }
    } catch (error) {
      console.warn('Cache validation failed:', error);
      clearAuthCache();
    }
    return null;
  });
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(false);
  const [userCreated, setUserCreated] = useState(false);

  // Persist user data to localStorage with timestamp
  const persistUser = (userData) => {
    if (userData) {
      const timestamp = Date.now().toString();
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
      localStorage.setItem(AUTH_STATE_KEY, 'authenticated');
      localStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp);
      localStorage.setItem(LAST_ACTIVITY_KEY, timestamp);
    } else {
      clearAuthCache();
    }
  };

  // Update last activity timestamp
  const updateActivity = () => {
    if (user) {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    }
  };

  const fetchUser = async (skipCache = false) => {
    console.log('🔑 AuthContext: fetchUser called', { skipCache, fetchingUser, user: !!user, authChecked });
    
    // Prevent multiple simultaneous calls
    if (fetchingUser && !skipCache) {
      console.log('🔑 AuthContext: Already fetching user, skipping');
      return;
    }
    
    // If we have valid cached user and not forcing refresh, return early
    if (!skipCache && user?.id && user?.email && !isCreatingUser && !sessionExpired && authChecked) {
      console.log('🔑 AuthContext: Using cached user data');
      setLoading(false);
      setAuthChecked(true);
      setInitializing(false);
      return;
    }
    
    console.log('🔑 AuthContext: Starting user fetch...');
    setFetchingUser(true);
    
    try {
      setLoading(true);
      console.log('🔑 AuthContext: Fetching auth session...');
      
      const session = await fetchAuthSession();
      console.log('🔑 AuthContext: Auth session result', { hasTokens: !!session.tokens });
      
      if (!session.tokens) {
        console.log('🔑 AuthContext: No tokens found, clearing user');
        setUser(null);
        setGroups([]);
        persistUser(null);
        return;
      }

      const userObj = await getCurrentUser();
      console.log('🔑 AuthContext: Current user result', { hasUser: !!userObj, userId: userObj?.userId });
      
      if (!userObj) {
        console.log('🔑 AuthContext: No current user found, clearing user');
        setUser(null);
        setGroups([]);
        persistUser(null);
        return;
      }

      let groups = [];
      let identityId = null;
      try {
        const idToken = session.tokens?.idToken;
        if (idToken) {
          setAuthToken(idToken.toString());
          const payload = idToken.payload;
          groups = payload["cognito:groups"] || [];
          identityId = session.identityId;
        }
      } catch (e) {
        groups = [];
      }
      setGroups(groups);
      
      // Fetch user attributes explicitly to get the name
      let userAttributes = {};
      try {
        userAttributes = await fetchUserAttributes();
      } catch (attrError) {
        console.error('Could not fetch user attributes:', attrError);
        userAttributes = userObj?.attributes || {};
      }
      
      // Ensure user exists in DB before proceeding
      const cognitoSub = userObj?.userId || userObj?.username;
      const email = userObj?.signInDetails?.loginId || userAttributes?.email || userObj?.attributes?.email;
      
      console.log('🔑 AuthContext: User identifiers', { cognitoSub, email });
      
      let userData = null;
      if (email && cognitoSub) {
        // First try to find user by email
        try {
          userData = await getUserByEmail(email);
        } catch (error) {
          // User not found by email, will try by username
        }
        
        // If not found by email, try by Cognito username (for existing users)
        if (!userData) {
          try {
            userData = await getUserByUsername(cognitoSub);
          } catch (error) {
            // User not found by Cognito username either
          }
        }

        // If user doesn't exist and we are not already creating one, create it.
        if (!userData && !isCreatingUser) {
          setIsCreatingUser(true); // Set lock

          try {
            if (!email) {
              throw new Error("Could not determine user's email during profile creation.");
            }

            // Get display name from various sources, avoid using UUID
            let displayName = userAttributes?.name || userObj?.attributes?.name;
            
            // If displayName is a UUID or empty, use email prefix
            if (!displayName || displayName === cognitoSub || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(displayName)) {
              displayName = email.split('@')[0];
            }
            

            // Create a user-friendly username from email or displayName
            const friendlyUsername = displayName.toLowerCase().replace(/[^a-z0-9]/g, '') || email.split('@')[0];
            
            userData = await createUser({
              identityId: identityId,
              id: cognitoSub,
              username: friendlyUsername, // Use a friendly username
              username_lowercase: friendlyUsername.toLowerCase(),
              email,
              displayName: displayName,
              displayName_lowercase: displayName.toLowerCase(),
              role: USER_ROLES.USER,
            });
            
            // Show success notification for new user creation
            setUserCreated(true);
            addNotification({
              type: 'success',
              message: `🎉 Welcome to HypeCircle, ${displayName}! Your account has been created successfully.`,
              duration: 6000
            });
          } catch (createError) {
            console.error('User creation failed, trying to fetch existing user:', createError);
            // If creation fails, try to fetch the user again (might already exist)
            try {
              userData = await getUserByEmail(email);
            } catch (emailError) {
              try {
                userData = await getUserByUsername(cognitoSub);
              } catch (fallbackError) {
                console.error('Fallback user lookup also failed:', fallbackError);
              }
            }
          } finally {
            setIsCreatingUser(false); // Release lock
          }
        }
      }

      if (!userData) {
        console.warn(`Could not find user profile for ${email}. The user might not be fully synced yet.`);
      }

      // Check if existing user has UUID display name and fix it
      if (userData && userData.displayName && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userData.displayName)) {
        const fixedDisplayName = userAttributes?.name || email.split('@')[0];
        try {
          const updatedUserData = await updateUser(userData.id, {
            displayName: fixedDisplayName,
            displayName_lowercase: fixedDisplayName.toLowerCase()
          });
          userData = updatedUserData;
        } catch (updateError) {
          console.error('Failed to fix display name:', updateError);
        }
      }

      // Ensure the final user object has the identityId and groups
      const finalUser = userData ? { ...userData, identityId, groups } : null;
      console.log('🔑 AuthContext: Setting user data', { finalUser: !!finalUser, userId: finalUser?.id });
      setUser(finalUser);
      persistUser(finalUser);
      
      // Mark auth as checked after successful fetch
      setAuthChecked(true);
      setInitializing(false);
      
      if (finalUser) {
        console.log('🎉 AuthContext: User successfully set!', finalUser.displayName || finalUser.email);
      }
    } catch (err) {
      // Handle the case when no user is signed in
      if (err.name === 'NoSignedUser' || err.message?.includes('No current user')) {
        setUser(null);
        setGroups([]);
        setError(null);
        clearAuthCache();
      } else if (err.name === 'NotAuthorizedException' || err.message?.includes('Access Token has expired')) {
        console.warn('Session expired, clearing cache');
        setSessionExpired(true);
        setUser(null);
        setGroups([]);
        setError('Session expired. Please sign in again.');
        clearAuthCache();
      } else {
        console.error('Error initializing user:', err);
        setError(err.message);
        setUser(null);
        setGroups([]);
        clearAuthCache();
      }
    } finally {
      setLoading(false);
      setFetchingUser(false);
      setSessionExpired(false);
      setAuthChecked(true);
      setInitializing(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let initTimeout;
    let idleCheckInterval;

    const initAuth = async () => {
      if (authChecked) return;
      
      try {
        await fetchUser();
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        if (isMounted) {
          setAuthChecked(true);
          setInitializing(false);
        }
      }
    };

    // Check for idle sessions periodically
    const checkIdleSession = () => {
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (lastActivity && user) {
        const timeSinceActivity = Date.now() - parseInt(lastActivity);
        if (timeSinceActivity > SESSION_TIMEOUT) {
          console.warn('Session expired due to inactivity');
          setSessionExpired(true);
          setUser(null);
          setGroups([]);
          clearAuthCache();
        }
      }
    };

    // Immediate initialization for cached users
    if (user?.id) {
      setAuthChecked(true);
      setInitializing(false);
      setLoading(false);
      return;
    }

    // Fallback timeout - ensures app doesn't hang if auth fails
    const fallbackTimeout = setTimeout(() => {
      if (isMounted && !authChecked) {
        console.warn('Auth initialization timeout, proceeding without auth');
        setAuthChecked(true);
        setInitializing(false);
        setLoading(false);
      }
    }, 8000); // Longer timeout to prevent premature warnings

    // Immediate initialization
    initTimeout = setTimeout(() => {
      if (isMounted) {
        initAuth();
      }
    }, 50); // Small delay to allow DOM to settle

    // Set up idle session checking
    idleCheckInterval = setInterval(checkIdleSession, IDLE_CHECK_INTERVAL);

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log('🔑 AuthContext: Hub event received', payload.event);
      if (!isMounted) return;
      
      switch (payload.event) {
        case 'signedIn':
        case 'signUp':
          console.log('🔑 AuthContext: Processing signedIn event');
          setSessionExpired(false);
          setError(null);
          setAuthChecked(false);
          setLoading(true);
          setFetchingUser(false); // Reset fetching state
          // Force immediate user fetch with minimal delay
          setTimeout(async () => {
            if (isMounted) {
              console.log('🔑 AuthContext: Starting fetchUser after signedIn');
              try {
                await fetchUser(true);
                console.log('🔑 AuthContext: fetchUser completed after signedIn');
              } catch (error) {
                console.error('🔑 AuthContext: Failed to fetch user after sign-in:', error);
                setLoading(false);
                setAuthChecked(true);
              }
            }
          }, 100); // Reduced delay for faster initialization
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
          setUser(null);
          setGroups([]);
          setAuthChecked(true); // Mark as checked after sign out
          clearAuthCache();
          break;
      }
    });

    // Listen for user activity to update timestamps
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => updateActivity();
    
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      isMounted = false;
      if (initTimeout) clearTimeout(initTimeout);
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
      if (idleCheckInterval) clearInterval(idleCheckInterval);
      
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      const { signOut: amplifySignOut } = await import('aws-amplify/auth');
      await amplifySignOut();
      setUser(null);
      setGroups([]);
      setError(null);
      setSessionExpired(false);
      clearAuthCache();
    } catch (error) {
      console.error('Sign out error:', error);
      // Force clear cache even if signOut fails
      setUser(null);
      setGroups([]);
      clearAuthCache();
    }
  };

  // Force clear all auth data and cache
  const clearSession = () => {
    setUser(null);
    setGroups([]);
    setError(null);
    setSessionExpired(false);
    clearAuthCache();
  };

  const hasRole = (role) => user?.role === role;
  const isAdmin = () => hasRole(USER_ROLES.ADMIN);
  const isModerator = () => hasRole(USER_ROLES.MODERATOR);

  // Restore updateUserProfile
  const updateUserProfile = async (updates) => {
    setLoading(true);
    try {
      if (!user || !user.id) {
        throw new Error('No user available to update');
      }
      // If displayName is part of the updates, add the lowercase version
      const updatePayload = { ...updates };
      if (updates.displayName) {
        updatePayload.displayName_lowercase = updates.displayName.toLowerCase();
      }

      const updatedUser = await updateUser(user.id, updatePayload);
      setUser(updatedUser);
      persistUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update user counts globally
  const updateUserCounts = (countUpdates) => {
    if (!user) return;
    
    const updatedUser = { ...user, ...countUpdates };
    setUser(updatedUser);
    persistUser(updatedUser);
  };

  const value = {
    user,
    identityId: user?.identityId,
    groups,
    loading: loading || initializing,
    error,
    sessionExpired,
    userCreated,
    isAuthenticated: !!user && !sessionExpired,
    signOut,
    clearSession,
    updateUserProfile,
    updateUserCounts,
    updateActivity,
    hasRole,
    isAdmin,
    isModerator,
    refreshUser: () => fetchUser(true)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};